import { z } from 'zod';
/** Inclusive bounds of the life-domain satisfaction rating, mirroring @zendia/domain. */
export const LIFE_DOMAIN_MIN_SATISFACTION = 1;
export const LIFE_DOMAIN_MAX_SATISFACTION = 5;
// Sub-schema for life domain weight
export const lifeDomainWeightSchema = z.object({
    lifeDomainId: z.string().min(1, 'Life domain ID cannot be empty'),
    weight: z.number().min(0, 'Weight must be at least 0').max(1, 'Weight cannot exceed 1'),
    // Nullish rather than a defaulted 0: "not rated yet" has to stay distinguishable
    // from "completely unsatisfied".
    satisfaction: z
        .number()
        .int()
        .min(LIFE_DOMAIN_MIN_SATISFACTION)
        .max(LIFE_DOMAIN_MAX_SATISFACTION)
        .nullish(),
});
/** 24-hour wall-clock time, 'HH:mm', mirroring @zendia/domain. */
export const TIME_OF_DAY_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
export const WORK_PATTERNS = ['weekday-fixed', 'shift', 'irregular', 'not-working'];
export const DAYS_OF_WEEK = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
];
/**
 * Discretionary-hours buckets and their midpoints, mirroring @zendia/domain. Buckets
 * rather than an exact number: asking for an integer of weekly free hours invites false
 * precision, and only the bucket is needed downstream.
 */
export const DISCRETIONARY_HOURS_BUCKETS = [
    { bucket: '0-2', midpoint: 1 },
    { bucket: '3-5', midpoint: 4 },
    { bucket: '6-10', midpoint: 8 },
    { bucket: '11-15', midpoint: 13 },
    { bucket: '15+', midpoint: 18 },
];
/** The bucket labels alone, as a non-empty tuple so `z.enum` infers the literal union. */
export const DISCRETIONARY_HOURS_BUCKET_VALUES = DISCRETIONARY_HOURS_BUCKETS.map((b) => b.bucket);
export const MAX_DISCRETIONARY_HOURS_PER_WEEK = 168;
/**
 * The user's actual week. Sent and stored whole, so every field is required once the
 * object is present at all; an empty `myDays` is a valid answer.
 */
export const timeCapacitySchema = z.object({
    wakeTime: z.string().regex(TIME_OF_DAY_PATTERN, 'Wake time must be a 24-hour time in HH:mm'),
    sleepTime: z.string().regex(TIME_OF_DAY_PATTERN, 'Sleep time must be a 24-hour time in HH:mm'),
    workPattern: z.enum(WORK_PATTERNS),
    myDays: z.array(z.enum(DAYS_OF_WEEK)),
    discretionaryHoursPerWeek: z.number().min(0).max(MAX_DISCRETIONARY_HOURS_PER_WEEK),
    discretionaryHoursBucket: z.enum(DISCRETIONARY_HOURS_BUCKET_VALUES),
});
/**
 * The physical-capacity vocabulary, mirroring @zendia/domain. The baseline order is
 * load-bearing: it is the ladder the starting dose steps along, which the API derives.
 */
export const ACTIVITY_BASELINES = [
    'mostly-sitting',
    'on-my-feet',
    'once-or-twice',
    'three-or-four',
    'most-days',
];
export const HEADROOMS = ['much-more', 'a-bit-more', 'about-the-same', 'less'];
export const ENERGY_WINDOWS = ['morning', 'midday', 'evening', 'varies'];
export const WORK_AROUND_IDS = [
    'on-my-feet-long',
    'high-impact',
    'lifting',
    'floor',
    'hands',
    'leaving-home',
    'busy-places',
    'screens-reading',
    'long-focus',
];
/** 'hard' demotes a suggestion, 'not-now' removes it. */
export const WORK_AROUND_LEVELS = ['hard', 'not-now'];
export const PHYSICAL_NOTE_MAX_LENGTH = 280;
export const physicalWorkAroundSchema = z.object({
    workAroundId: z.enum(WORK_AROUND_IDS),
    level: z.enum(WORK_AROUND_LEVELS),
});
/**
 * What the user answered on the physical-capacity screen. This is the shape that is sent:
 * the starting dose is derived from the baseline and headroom by the API, so it is never
 * part of an update.
 */
export const physicalCapacityUpdateSchema = z.object({
    baseline: z.enum(ACTIVITY_BASELINES),
    headroom: z.enum(HEADROOMS),
    energyWindow: z.enum(ENERGY_WINDOWS),
    workArounds: z.array(physicalWorkAroundSchema),
    nothingInTheWay: z.boolean(),
    note: z.string().max(PHYSICAL_NOTE_MAX_LENGTH),
});
/** What is read back: the same answers plus the dose the API derived from them. */
export const physicalCapacitySchema = physicalCapacityUpdateSchema.extend({
    startingSessionMinutes: z.number(),
    startingSessionsPerWeek: z.number(),
});
/** How the Tasks panel renders each task row, mirroring @zendia/domain. */
export const TASKS_VIEWS = ['expanded', 'condensed'];
/** The Settings screen's Appearance choice, mirroring @zendia/domain. */
export const THEME_PREFERENCES = ['system', 'light', 'dark'];
/** Display preferences, mirroring @zendia/domain. */
export const TIME_FORMATS = ['12h', '24h'];
export const DATE_FORMATS = ['MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'];
export const WEEK_START_DAYS = DAYS_OF_WEEK;
// UserSettings schema matching the backend
export const userSettingsSchema = z.object({
    id: z.string(),
    name: z.string().nullish(),
    language: z.string().nullish(),
    region: z.string().nullish(),
    defaultTimeZone: z.string().nullish(),
    onboardingStep: z.number().nullish(),
    coreValues: z.array(z.string()).optional(),
    antiValues: z.array(z.string()).optional(),
    lifeDomains: z.array(lifeDomainWeightSchema).optional(),
    timeCapacity: timeCapacitySchema.nullish(),
    physicalCapacity: physicalCapacitySchema.nullish(),
    tasksView: z.enum(TASKS_VIEWS).nullish(),
    themePreference: z.enum(THEME_PREFERENCES).nullish(),
    timeFormat: z.enum(TIME_FORMATS).nullish(),
    dateFormat: z.enum(DATE_FORMATS).nullish(),
    weekStart: z.enum(WEEK_START_DAYS).nullish(),
});
// Form data for updating user settings (partial updates supported)
export const userSettingsUpdateSchema = z.object({
    name: z.string().trim().nullish(),
    language: z.string().trim().nullish(),
    region: z.string().trim().nullish(),
    defaultTimeZone: z.string().trim().nullish(),
    onboardingStep: z.number().int().nonnegative().nullish(),
    coreValues: z.array(z.string()).optional(),
    antiValues: z.array(z.string()).optional(),
    lifeDomains: z.array(lifeDomainWeightSchema).optional(),
    timeCapacity: timeCapacitySchema.optional(),
    physicalCapacity: physicalCapacityUpdateSchema.optional(),
    tasksView: z.enum(TASKS_VIEWS).optional(),
    themePreference: z.enum(THEME_PREFERENCES).optional(),
    timeFormat: z.enum(TIME_FORMATS).optional(),
    dateFormat: z.enum(DATE_FORMATS).optional(),
    weekStart: z.enum(WEEK_START_DAYS).optional(),
});
